import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';

/**
 * One splitter's outputs as buttons, the taken ones named and disabled.
 *
 * Shared between the ONU's Place dialog and the authorise wizard so the two
 * doors that record a splitter port can never disagree about which ports are
 * offered — both read `takenPorts` from the same endpoint the API's own
 * free-port refusal reads, so a port this grid offers is one the save will
 * take.
 *
 * Read from the ONUs rather than the TR-069 device rows, which are empty on a
 * fleet managed over OMCI — the ports would all have looked free.
 *
 * The port is optional everywhere it is asked for, so the chosen button is a
 * toggle: pressing it again puts the answer back to "not recorded".
 */
export function SplitterPortGrid({
  splitterId,
  capacity,
  value,
  onChange,
}: {
  splitterId: string;
  capacity: number;
  /** The chosen port as the form holds it — '' for none. */
  value: string;
  onChange: (port: string) => void;
}) {
  // Which ports of the chosen splitter are already spoken for, so the picker
  // cannot put two subscribers on one fibre.
  const taken = useQuery({
    queryKey: ['splitter-onus', splitterId],
    queryFn: () =>
      api.get<{ takenPorts?: number[] }>(`/splitters/${encodeURIComponent(splitterId)}/onus`),
    enabled: Boolean(splitterId),
  });
  const usedPorts = new Set(taken.data?.takenPorts ?? []);

  return (
    <div className="flex flex-wrap gap-1.5">
      {Array.from({ length: capacity }, (_, i) => i + 1).map((n) => {
        const used = usedPorts.has(n);
        const chosen = String(n) === value;
        return (
          <button
            key={n}
            type="button"
            disabled={used}
            onClick={() => onChange(chosen ? '' : String(n))}
            title={used ? 'Already used by another ONU' : `Port ${n}`}
            className={`h-8 w-8 rounded-lg border text-xs tabular-nums transition-colors ${
              chosen
                ? 'border-primary bg-primary-soft text-primary'
                : used
                  ? 'cursor-not-allowed border-line text-faint opacity-40'
                  : 'border-line text-muted hover:bg-surface-2 hover:text-ink'
            }`}
          >
            {n}
          </button>
        );
      })}
    </div>
  );
}
